import { useCallback, useEffect, useRef } from 'react';
import type { TabId } from '../../../components/mui/common/Tabs';
import type { RecordPosition } from '../../agendaRecord/recordPosition';
import { getTabKey, readRecordPosition, updateRecordPosition } from '../../agendaRecord/recordPosition';
import { getScrollableParent } from '../../scroll/scrollExpandedSectionIntoView';

const SCROLL_WRITE_DELAY = 250;
const SCROLL_RESTORE_DELAY = 50;
const SCROLL_RESTORE_ATTEMPTS = 10;

interface UseRecordPositionOptions {
  agendaId: string;
  recordId: string;
  tab: TabId;
  containerRef?: { readonly current: HTMLElement | null };
  enabled?: boolean;
}

interface UseRecordPositionReturn {
  getSectionDefaultExpanded: (sectionId: string, fallback: boolean, sectionTab?: TabId) => boolean;
  handleSectionExpandedChange: (sectionId: string, expanded: boolean) => void;
}

export const useRecordPosition = ({ agendaId, recordId, tab, containerRef, enabled = true }: UseRecordPositionOptions): UseRecordPositionReturn => {
  const tabKey = getTabKey(tab);
  const recordKey = `${agendaId}:${recordId}`;

  // Read the stored position during the first render of a record so sections mount already expanded.
  const positionRef = useRef<{ recordKey: string; position: RecordPosition } | null>(null);

  if (positionRef.current === null || positionRef.current.recordKey !== recordKey) {
    positionRef.current = { recordKey, position: readRecordPosition(agendaId, recordId) };
  }

  const tabKeyRef = useRef(tabKey);
  const recordRef = useRef({ agendaId, recordId });
  const scrollTopRef = useRef(0);
  const writeTimeoutRef = useRef<number | null>(null);
  const scrollerRef = useRef<HTMLElement | null | undefined>(undefined);

  const getScroller = useCallback((): HTMLElement | null => {
    const cached = scrollerRef.current;

    if (cached?.isConnected) return cached;

    const container = containerRef?.current;
    const scroller = container ? getScrollableParent(container) : null;

    scrollerRef.current = scroller;
    return scroller;
  }, [containerRef]);

  const flushScroll = useCallback((): void => {
    if (writeTimeoutRef.current !== null) {
      window.clearTimeout(writeTimeoutRef.current);
      writeTimeoutRef.current = null;
    }

    const { agendaId: flushAgendaId, recordId: flushRecordId } = recordRef.current;
    const flushTabKey = tabKeyRef.current;

    if (!flushAgendaId || !flushRecordId) return;

    const scrollTop = scrollTopRef.current;

    updateRecordPosition(flushAgendaId, flushRecordId, (current) => {
      const currentTabPosition = current.tabs[flushTabKey];

      if (currentTabPosition?.scrollTop === scrollTop) return current;

      return {
        ...current,
        tabs: {
          ...current.tabs,
          [flushTabKey]: {
            scrollTop,
            sectionStates: currentTabPosition?.sectionStates ?? {},
          },
        },
      };
    });
  }, []);

  const getSectionDefaultExpanded = useCallback(
    (sectionId: string, fallback: boolean, sectionTab?: TabId): boolean => {
      if (!sectionId) return fallback;

      const storedExpanded = positionRef.current?.position.tabs[getTabKey(sectionTab ?? tabKey)]?.sectionStates[sectionId];

      return typeof storedExpanded === 'boolean' ? storedExpanded : fallback;
    },
    [tabKey]
  );

  const handleSectionExpandedChange = useCallback(
    (sectionId: string, expanded: boolean): void => {
      if (!agendaId || !recordId || !sectionId) return;

      const next = updateRecordPosition(agendaId, recordId, (current) => {
        const currentTabPosition = current.tabs[tabKey];

        if (currentTabPosition?.sectionStates[sectionId] === expanded) return current;

        return {
          ...current,
          tabs: {
            ...current.tabs,
            [tabKey]: {
              scrollTop: currentTabPosition?.scrollTop ?? 0,
              sectionStates: { ...currentTabPosition?.sectionStates, [sectionId]: expanded },
            },
          },
        };
      });

      // Sections remount when their tab is switched, so the cache has to follow the latest write.
      if (next && positionRef.current?.recordKey === recordKey) {
        positionRef.current = { recordKey, position: next };
      }
    },
    [agendaId, recordId, recordKey, tabKey]
  );

  // The active tab is also restored on reload/save, when the URL does not carry it anymore.
  useEffect(() => {
    if (!agendaId || !recordId) return;

    updateRecordPosition(agendaId, recordId, (current) => (current.activeTab === tabKey ? current : { ...current, activeTab: tabKey }));
  }, [agendaId, recordId, tabKey]);

  // Save the position of the tab the user is leaving before switching the tracked tab/record.
  useEffect(() => {
    const isRecordChanged = recordRef.current.agendaId !== agendaId || recordRef.current.recordId !== recordId;

    if (!isRecordChanged && tabKeyRef.current === tabKey) return;

    flushScroll();

    tabKeyRef.current = tabKey;
    recordRef.current = { agendaId, recordId };
    scrollTopRef.current = 0;
  }, [agendaId, flushScroll, recordId, tabKey]);

  // Restore the scroll offset of the tab the user comes back to.
  useEffect(() => {
    if (!enabled || !agendaId || !recordId) return;

    const storedScrollTop = readRecordPosition(agendaId, recordId).tabs[tabKey]?.scrollTop ?? 0;

    scrollTopRef.current = storedScrollTop;

    let restoreTimeout: number;

    const restoreScroll = (remainingAttempts: number): void => {
      const scroller = getScroller();

      const canReachStoredOffset = scroller && scroller.scrollHeight - scroller.clientHeight >= storedScrollTop;

      if (scroller && (canReachStoredOffset || remainingAttempts === 0)) {
        scroller.scrollTo({ top: storedScrollTop });
        return;
      }

      if (containerRef?.current && remainingAttempts > 0) {
        restoreTimeout = window.setTimeout(() => restoreScroll(remainingAttempts - 1), SCROLL_RESTORE_DELAY);
        return;
      }

      window.scrollTo({ top: storedScrollTop });
    };

    restoreTimeout = window.setTimeout(() => restoreScroll(SCROLL_RESTORE_ATTEMPTS), SCROLL_RESTORE_DELAY);

    return () => window.clearTimeout(restoreTimeout);
  }, [agendaId, containerRef, enabled, getScroller, recordId, tabKey]);

  useEffect(() => {
    if (!enabled) return;

    const handleScroll = (event: Event): void => {
      const scroller = getScroller();
      const target = event.target;
      const isWindowEvent = target === document || target === window;

      // Section content can contain its own scrollable elements, only the record content scroll counts.
      if (scroller ? target !== scroller : !isWindowEvent) return;

      scrollTopRef.current = scroller ? scroller.scrollTop : window.scrollY;

      if (writeTimeoutRef.current !== null) return;

      writeTimeoutRef.current = window.setTimeout(() => {
        writeTimeoutRef.current = null;
        flushScroll();
      }, SCROLL_WRITE_DELAY);
    };

    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    window.addEventListener('pagehide', flushScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('pagehide', flushScroll);
      flushScroll();
    };
  }, [enabled, flushScroll, getScroller]);

  return {
    getSectionDefaultExpanded,
    handleSectionExpandedChange,
  };
};
